import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import ReactECharts from 'echarts-for-react'
import { useApi } from '../../hooks/useApi'
import { apiPost, apiDelete } from '../../lib/auth'
import { AuditProgress } from '../../components/llm/AuditProgress'
import { ArrowLeft, Play, Trash2, Eye, ChevronDown, ChevronUp, Archive, Info } from 'lucide-react'
import { LlmLogo } from '../../components/icons/LlmLogos'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AuditCost {
  provider: string
  model: string
  tokensIn: number
  tokensOut: number
  cost: string
}

interface Audit {
  id: number
  status: string
  startedAt: string | null
  completedAt: string | null
  error: string | null
  result: any
  progress: any
  createdAt: string
  costs: AuditCost[]
}

interface Campaign {
  id: number
  name: string
  brand: string
  status: string
  brandVariants: string[]
  competitors: string[]
  sector: string | null
  locations: string[]
  runsPerQuery: number
  queries: any[]
  contextFields: any
  createdAt: string
  updatedAt: string
  client: { userId: number; name: string }
  audits: Audit[]
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function scoreColor(score: number): string {
  if (score >= 60) return '#22C55E'
  if (score >= 40) return '#F59E0B'
  return '#EF4444'
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function durationMinutes(start: string | null, end: string | null): string {
  if (!start || !end) return '-'
  const ms = new Date(end).getTime() - new Date(start).getTime()
  const mins = Math.round(ms / 60_000)
  return mins < 1 ? '< 1 min' : `${mins} min`
}

function totalCost(costs: AuditCost[]): string {
  const sum = costs.reduce((acc, c) => acc + parseFloat(c.cost), 0)
  return sum.toFixed(4)
}

function statusBadge(status: string) {
  const map: Record<string, { bg: string; text: string; label: string }> = {
    pending:   { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'En attente' },
    running:   { bg: 'bg-blue-100',   text: 'text-blue-700',   label: 'En cours' },
    completed: { bg: 'bg-green-100',  text: 'text-green-700',  label: 'Terminé' },
    failed:    { bg: 'bg-red-100',    text: 'text-red-700',    label: 'Échoué' },
  }
  const s = map[status] ?? { bg: 'bg-gray-100', text: 'text-gray-600', label: status }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${s.bg} ${s.text}`}>
      {s.label}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Gauge option builder
// ---------------------------------------------------------------------------

function gaugeOption(score: number, label: string) {
  const color = scoreColor(score)
  return {
    series: [
      {
        type: 'gauge',
        startAngle: 220,
        endAngle: -40,
        radius: '90%',
        min: 0,
        max: 100,
        progress: { show: true, width: 10, roundCap: true, itemStyle: { color } },
        pointer: { show: false },
        axisLine: { lineStyle: { width: 10, color: [[1, '#E2E4ED']] } },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: { show: false },
        detail: {
          valueAnimation: true,
          fontSize: 22,
          fontWeight: 700,
          color: '#1B2559',
          offsetCenter: [0, '20%'],
          formatter: '{value}',
        },
        title: {
          offsetCenter: [0, '55%'],
          fontSize: 12,
          color: '#555A73',
        },
        data: [{ value: Math.round(score), name: label }],
      },
    ],
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function LlmCampaignDetail() {
  const { projectId, campaignId } = useParams()
  const navigate = useNavigate()
  const { data: campaign, loading, error, refetch } = useApi<Campaign>(
    `/api/llm/campaigns/${campaignId}`,
  )

  const [launching, setLaunching] = useState(false)
  const [deleting, setDeleting] = useState<number | null>(null)
  const [configOpen, setConfigOpen] = useState(false)
  const [polledProgress, setPolledProgress] = useState<any>(null)
  const [polledStatus, setPolledStatus] = useState<string | null>(null)
  const [auditStartTime, setAuditStartTime] = useState<number | null>(null)
  const [elapsed, setElapsed] = useState(0)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // ---- Find active (pending/running) audit ----
  const activeAudit = campaign?.audits.find(
    (a) => a.status === 'pending' || a.status === 'running',
  )

  // ---- Elapsed timer ----
  useEffect(() => {
    if (activeAudit) {
      const start = activeAudit.startedAt ? new Date(activeAudit.startedAt).getTime() : Date.now()
      setAuditStartTime(start)
      setElapsed(Math.floor((Date.now() - start) / 1000))
      timerRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - start) / 1000))
      }, 1000)
    } else {
      setAuditStartTime(null)
      setElapsed(0)
      setPolledProgress(null)
      setPolledStatus(null)
      if (timerRef.current) clearInterval(timerRef.current)
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [activeAudit?.id])

  // ---- Poll active audit ----
  useEffect(() => {
    if (!activeAudit) {
      if (pollRef.current) clearInterval(pollRef.current)
      return
    }

    const poll = async () => {
      try {
        const res = await fetch(`/api/llm/audits/${activeAudit.id}/poll`, {
          credentials: 'include',
        })
        const json = await res.json()
        const d = json.data ?? json
        setPolledStatus(d.status)
        if (d.progress) setPolledProgress(d.progress)
        if (d.status === 'completed' || d.status === 'failed') {
          if (pollRef.current) clearInterval(pollRef.current)
          if (timerRef.current) clearInterval(timerRef.current)
          refetch()
        }
      } catch {
        // ignore transient errors
      }
    }

    poll()
    pollRef.current = setInterval(poll, 4000)
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [activeAudit?.id])

  // ---- Launch audit ----
  const handleLaunch = async () => {
    setLaunching(true)
    const { error: err } = await apiPost(`/api/llm/campaigns/${campaignId}/audits`, {})
    setLaunching(false)
    if (err) return alert(err)
    refetch()
  }

  // ---- Delete audit ----
  const handleDeleteAudit = async (auditId: number) => {
    if (!confirm('Supprimer cet audit ?')) return
    setDeleting(auditId)
    const { error: err } = await apiDelete(`/api/llm/audits/${auditId}`)
    setDeleting(null)
    if (err) return alert(err)
    refetch()
  }

  // ---- Loading / error states ----
  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#6366F1] border-t-transparent" />
      </div>
    )
  }

  if (error || !campaign) {
    return (
      <div className="rounded-xl border border-red-300 bg-red-50 p-6 text-center text-red-600">
        {error ?? 'Campagne introuvable'}
      </div>
    )
  }

  // ---- Normalize arrays that might be null from Prisma ----
  const cBrandVariants = campaign.brandVariants ?? []
  const cCompetitors = campaign.competitors ?? []
  const cLocations = campaign.locations ?? []
  const cQueries: any[] = (campaign.queries as any) ?? []

  // ---- Derived data ----
  const audits = campaign.audits ?? []
  const completedAudits = audits
    .filter((a) => a.status === 'completed' && a.result?.scores)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

  const latestAudit = completedAudits[0] ?? null
  const scores = latestAudit?.result?.scores as
    | { overall?: number; global?: number; chatgpt: number; gemini: number; claude: number }
    | undefined

  const rawCompetitorData = latestAudit?.result?.competitors as
    | Record<string, number | { global: number }>
    | undefined
  // Normalize: backend returns Record<string, number>, UI needs .global
  const competitorData = rawCompetitorData
    ? Object.fromEntries(
        Object.entries(rawCompetitorData).map(([k, v]) => [
          k,
          typeof v === 'number' ? { global: v } : v,
        ]),
      ) as Record<string, { global: number }>
    : undefined

  // ---- Evolution data (oldest first) ----
  const evolutionAudits = [...completedAudits].reverse()

  return (
    <div className="space-y-6">
      {/* ================================================================ */}
      {/* HEADER                                                          */}
      {/* ================================================================ */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link
            to={projectId ? `/p/${projectId}/ai/llm` : '/ai/llm'}
            className="text-[#555A73] hover:text-[#1B2559] transition-colors"
          >
            <ArrowLeft size={20} />
          </Link>
          <div>
            <h1 className="text-xl font-bold text-[#1B2559]">{campaign.name}</h1>
            <p className="text-sm text-[#555A73]">
              Marque : {campaign.brand} &middot; {cQueries.length} requ&ecirc;tes
            </p>
          </div>
          {statusBadge(campaign.status)}
        </div>

        <button
          onClick={handleLaunch}
          disabled={launching || !!activeAudit}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-[#6366F1] hover:bg-[#4F46E5] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Play size={16} />
          {launching ? 'Lancement...' : 'Lancer un audit'}
        </button>
      </div>

      {/* ---- Info banner ---- */}
      <div className="rounded-xl border border-[#E2E4ED] bg-[#F8F9FC] px-5 py-3 flex items-start gap-3">
        <Info size={16} className="text-[#6366F1] flex-shrink-0 mt-0.5" />
        <p className="text-xs text-[#555A73] leading-relaxed">
          Les audits interrogent ChatGPT, Gemini et Claude avec la <span className="font-medium text-[#1B2559]">recherche web activée</span> — les résultats reflètent ce qu'un utilisateur réel obtiendrait en posant la même question aujourd'hui. Le score mesure le taux de citation de votre marque dans les réponses de chaque modèle.
        </p>
      </div>

      {/* ---- Active audit progress ---- */}
      {activeAudit && (
        <div className="rounded-xl border border-blue-200 bg-blue-50/50 p-5 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-blue-500 animate-pulse" />
              <span className="text-sm font-medium text-blue-700">
                {polledStatus === 'pending' ? "Préparation de l'audit..." : 'Audit en cours'}
              </span>
            </div>
            <span className="text-sm tabular-nums text-blue-600 font-mono">
              {Math.floor(elapsed / 60).toString().padStart(2, '0')}:{(elapsed % 60).toString().padStart(2, '0')}
            </span>
          </div>
          <AuditProgress progress={polledProgress ?? activeAudit.progress} />
        </div>
      )}

      {/* ================================================================ */}
      {/* SCORE CARDS                                                      */}
      {/* ================================================================ */}
      {scores ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {(
            [
              ['Global', (scores.overall ?? scores.global ?? 0)],
              ['ChatGPT', scores.chatgpt],
              ['Gemini', scores.gemini],
              ['Claude', scores.claude],
            ] as [string, number][]
          ).map(([label, val]) => (
            <div
              key={label}
              className="rounded-xl border border-[#E2E4ED] bg-white p-3 flex flex-col items-center"
            >
              <div className="flex items-center gap-1.5 mb-1">
                {label !== 'Global' && <LlmLogo llm={label} size={18} />}
                <span className="text-xs font-medium text-[#555A73]">{label}</span>
              </div>
              <ReactECharts
                option={gaugeOption(val, '')}
                style={{ height: 140, width: '100%' }}
                opts={{ renderer: 'svg' }}
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-xl border border-[#E2E4ED] bg-white p-8 text-center">
          <Archive size={32} className="mx-auto mb-2 text-[#E2E4ED]" />
          <p className="text-[#555A73] text-sm">
            Aucun audit termin&eacute;. Lancez un premier audit pour voir les scores.
          </p>
        </div>
      )}

      {/* ================================================================ */}
      {/* EVOLUTION CHART                                                  */}
      {/* ================================================================ */}
      {evolutionAudits.length >= 2 && (
        <div className="rounded-xl border border-[#E2E4ED] bg-white p-5">
          <h2 className="text-base font-semibold text-[#1B2559] mb-4">&Eacute;volution des scores</h2>
          <ReactECharts
            style={{ height: 320 }}
            opts={{ renderer: 'svg' }}
            option={{
              tooltip: { trigger: 'axis' },
              legend: {
                data: ['Global', 'ChatGPT', 'Gemini', 'Claude'],
                bottom: 0,
                textStyle: { color: '#555A73', fontSize: 12 },
              },
              grid: { top: 10, right: 20, bottom: 40, left: 40 },
              xAxis: {
                type: 'category',
                data: evolutionAudits.map((a) => formatDate(a.createdAt)),
                axisLabel: { color: '#555A73', fontSize: 11 },
                axisLine: { lineStyle: { color: '#E2E4ED' } },
              },
              yAxis: {
                type: 'value',
                min: 0,
                max: 100,
                axisLabel: { color: '#555A73', fontSize: 11 },
                splitLine: { lineStyle: { color: '#E2E4ED' } },
              },
              series: [
                {
                  name: 'Global',
                  type: 'line',
                  data: evolutionAudits.map((a) => Math.round(a.result.scores.overall ?? a.result.scores.global ?? 0)),
                  lineStyle: { width: 3, color: '#6366F1' },
                  itemStyle: { color: '#6366F1' },
                  symbol: 'circle',
                  symbolSize: 6,
                },
                {
                  name: 'ChatGPT',
                  type: 'line',
                  data: evolutionAudits.map((a) => Math.round(a.result.scores.chatgpt)),
                  lineStyle: { width: 2, color: '#22C55E' },
                  itemStyle: { color: '#22C55E' },
                  symbol: 'circle',
                  symbolSize: 5,
                },
                {
                  name: 'Gemini',
                  type: 'line',
                  data: evolutionAudits.map((a) => Math.round(a.result.scores.gemini)),
                  lineStyle: { width: 2, color: '#3B82F6' },
                  itemStyle: { color: '#3B82F6' },
                  symbol: 'circle',
                  symbolSize: 5,
                },
                {
                  name: 'Claude',
                  type: 'line',
                  data: evolutionAudits.map((a) => Math.round(a.result.scores.claude)),
                  lineStyle: { width: 2, color: '#F59E0B' },
                  itemStyle: { color: '#F59E0B' },
                  symbol: 'circle',
                  symbolSize: 5,
                },
              ],
            }}
          />
        </div>
      )}

      {/* ================================================================ */}
      {/* COMPETITOR COMPARISON                                            */}
      {/* ================================================================ */}
      {competitorData && Object.keys(competitorData).length > 0 && scores && (
        <div className="rounded-xl border border-[#E2E4ED] bg-white p-5">
          <h2 className="text-base font-semibold text-[#1B2559] mb-4">Comparaison concurrents</h2>
          <ReactECharts
            style={{ height: Math.max(200, (Object.keys(competitorData).length + 1) * 40 + 60) }}
            opts={{ renderer: 'svg' }}
            option={{
              tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
              grid: { top: 10, right: 30, bottom: 30, left: 120 },
              xAxis: {
                type: 'value',
                min: 0,
                max: 100,
                axisLabel: { color: '#555A73', fontSize: 11 },
                splitLine: { lineStyle: { color: '#E2E4ED' } },
              },
              yAxis: {
                type: 'category',
                data: [
                  campaign.brand,
                  ...Object.keys(competitorData).sort(
                    (a, b) => competitorData[b].global - competitorData[a].global,
                  ),
                ],
                axisLabel: { color: '#1B2559', fontSize: 12 },
                axisLine: { lineStyle: { color: '#E2E4ED' } },
              },
              series: [
                {
                  type: 'bar',
                  data: [
                    { value: Math.round((scores.overall ?? scores.global ?? 0)), itemStyle: { color: '#6366F1' } },
                    ...Object.keys(competitorData)
                      .sort((a, b) => competitorData[b].global - competitorData[a].global)
                      .map((k) => ({
                        value: Math.round(competitorData[k].global),
                        itemStyle: { color: '#CBD5E1' },
                      })),
                  ],
                  barWidth: 18,
                  label: {
                    show: true,
                    position: 'right',
                    fontSize: 11,
                    color: '#555A73',
                  },
                },
              ],
            }}
          />
        </div>
      )}

      {/* ================================================================ */}
      {/* AUDITS TABLE                                                     */}
      {/* ================================================================ */}
      <div className="rounded-xl border border-[#E2E4ED] bg-white">
        <div className="px-5 py-4 border-b border-[#E2E4ED]">
          <h2 className="text-base font-semibold text-[#1B2559]">Historique des audits</h2>
        </div>

        {audits.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#555A73]">Aucun audit</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E2E4ED] text-left text-xs text-[#555A73] uppercase tracking-wider">
                  <th className="px-5 py-3">Date</th>
                  <th className="px-5 py-3">Score</th>
                  <th className="px-5 py-3">Statut</th>
                  <th className="px-5 py-3">Dur&eacute;e</th>
                  <th className="px-5 py-3">Tokens</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {[...audits]
                  .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                  .map((audit) => {
                    const auditScore = audit.result?.scores?.global as number | undefined
                    return (
                      <tr
                        key={audit.id}
                        className="border-b border-[#E2E4ED] last:border-0 hover:bg-[#F8F9FC] transition-colors"
                      >
                        <td className="px-5 py-3 text-[#1B2559]">{formatDate(audit.createdAt)}</td>
                        <td className="px-5 py-3">
                          {auditScore != null ? (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold text-white"
                              style={{ backgroundColor: scoreColor(auditScore) }}
                            >
                              {Math.round(auditScore)}
                            </span>
                          ) : (
                            <span className="text-[#8B8FA7]">-</span>
                          )}
                        </td>
                        <td className="px-5 py-3">{statusBadge(audit.status)}</td>
                        <td className="px-5 py-3 text-[#555A73]">
                          {durationMinutes(audit.startedAt, audit.completedAt)}
                        </td>
                        <td className="px-5 py-3 text-[#555A73]">
                          {audit.costs?.length > 0
                            ? `${audit.costs.reduce((s: number, c: AuditCost) => s + c.tokensIn + c.tokensOut, 0).toLocaleString()}`
                            : '-'}
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center justify-end gap-2">
                            {audit.status === 'completed' && (
                              <Link
                                to={
                                  projectId
                                    ? `/p/${projectId}/ai/llm/${campaignId}/${audit.id}`
                                    : `/ai/llm/${campaignId}/${audit.id}`
                                }
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-[#6366F1] bg-[#6366F1]/10 hover:bg-[#6366F1]/20 transition-colors"
                              >
                                <Eye size={14} />
                                D&eacute;tail
                              </Link>
                            )}
                            <button
                              onClick={() => handleDeleteAudit(audit.id)}
                              disabled={deleting === audit.id}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 disabled:opacity-50 transition-colors"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ================================================================ */}
      {/* CONFIGURATION (collapsed)                                        */}
      {/* ================================================================ */}
      <div className="rounded-xl border border-[#E2E4ED] bg-white">
        <button
          onClick={() => setConfigOpen(!configOpen)}
          className="w-full flex items-center justify-between px-5 py-4 text-left"
        >
          <h2 className="text-base font-semibold text-[#1B2559]">Configuration</h2>
          {configOpen ? (
            <ChevronUp size={18} className="text-[#555A73]" />
          ) : (
            <ChevronDown size={18} className="text-[#555A73]" />
          )}
        </button>

        {configOpen && (
          <div className="px-5 pb-5 space-y-4 border-t border-[#E2E4ED] pt-4">
            {/* Brand info */}
            <div>
              <h3 className="text-sm font-medium text-[#1B2559] mb-1">Marque</h3>
              <p className="text-sm text-[#555A73]">{campaign.brand}</p>
              {cBrandVariants.length > 0 && (
                <p className="text-xs text-[#8B8FA7] mt-0.5">
                  Variantes : {cBrandVariants.join(', ')}
                </p>
              )}
            </div>

            {/* Competitors */}
            {cCompetitors.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-[#1B2559] mb-1">Concurrents</h3>
                <div className="flex flex-wrap gap-1.5">
                  {cCompetitors.map((c) => (
                    <span
                      key={c}
                      className="inline-flex px-2 py-0.5 rounded-full text-xs bg-gray-100 text-[#555A73]"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Sector / locations */}
            <div className="grid grid-cols-2 gap-4">
              {campaign.sector && (
                <div>
                  <h3 className="text-sm font-medium text-[#1B2559] mb-1">Secteur</h3>
                  <p className="text-sm text-[#555A73]">{campaign.sector}</p>
                </div>
              )}
              {cLocations.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-[#1B2559] mb-1">Localisations</h3>
                  <p className="text-sm text-[#555A73]">{cLocations.join(', ')}</p>
                </div>
              )}
            </div>

            {/* Queries */}
            <div>
              <h3 className="text-sm font-medium text-[#1B2559] mb-2">
                Requ&ecirc;tes ({cQueries.length})
              </h3>
              <div className="space-y-1 max-h-60 overflow-y-auto">
                {cQueries.map((q: any, i: number) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 text-sm text-[#555A73] py-1 px-2 rounded hover:bg-[#F8F9FC]"
                  >
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-[#6366F1]/10 text-[#6366F1] text-[10px] font-bold flex-shrink-0">
                      {q.category ?? '-'}
                    </span>
                    <span className="truncate">{q.text ?? q.query ?? JSON.stringify(q)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Context fields */}
            {campaign.contextFields && Object.keys(campaign.contextFields).length > 0 && (() => {
              const labels: Record<string, string> = {
                businessType: 'Type de business',
                targetPersona: 'Persona cible',
                searchIntents: 'Intentions de recherche',
                maturityLevel: 'Niveau de maturité',
                knownKeywords: 'Mots-clés connus',
                additionalContext: 'Contexte supplémentaire',
                geoZones: 'Zones géographiques',
                services: 'Services/produits',
                brandVariants: 'Variantes de marque',
                competitors: 'Concurrents',
                sector: 'Secteur',
                locations: 'Localisations',
              }
              const entries = Object.entries(campaign.contextFields as Record<string, any>).filter(([, v]) => v && (typeof v === 'string' ? v.trim() : Array.isArray(v) ? v.length > 0 : true))
              if (!entries.length) return null
              return (
                <div>
                  <h3 className="text-sm font-medium text-[#1B2559] mb-2">Contexte</h3>
                  <div className="space-y-2.5">
                    {entries.map(([key, val]) => (
                      <div key={key}>
                        <span className="text-xs font-medium text-[#1B2559] uppercase tracking-wider">{labels[key] ?? key}</span>
                        <p className="text-sm text-[#555A73] mt-0.5 leading-relaxed">
                          {Array.isArray(val) ? val.join(', ') : String(val)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })()}
          </div>
        )}
      </div>
    </div>
  )
}
